import { useEffect, useRef } from "react";

// Continuously auto-scrolls a ref'd container at a fixed pixel-per-second rate.
// When it reaches the end it loops back to the start (jumps, doesn't easing-bounce).
// Pauses on hover and when the page tab is hidden so we don't burn battery off-screen.
//
// Returns a callback ref to attach to the scrollable element.
const useAutoScroll = ({
  enabled = true,
  direction = "down",   // "up" | "down" | "left" | "right"
  speed = 30,           // px per second
  pauseOnHover = true,
} = {}) => {
  const nodeRef = useRef(null);
  const rafRef = useRef(null);
  const lastTsRef = useRef(0);
  const pausedRef = useRef(false);

  useEffect(() => {
    const node = nodeRef.current;
    if (!enabled || !node || speed <= 0) return undefined;

    const isHorizontal = direction === "left" || direction === "right";
    const dirSign = (direction === "up" || direction === "left") ? -1 : 1;

    const onEnter = () => { if (pauseOnHover) pausedRef.current = true; };
    const onLeave = () => { pausedRef.current = false; };
    const onVisibility = () => {
      pausedRef.current = document.hidden ? true : pausedRef.current;
    };
    node.addEventListener("mouseenter", onEnter);
    node.addEventListener("mouseleave", onLeave);
    document.addEventListener("visibilitychange", onVisibility);

    const tick = (ts) => {
      if (!lastTsRef.current) lastTsRef.current = ts;
      const dt = (ts - lastTsRef.current) / 1000;
      lastTsRef.current = ts;

      if (!pausedRef.current) {
        const delta = speed * dt * dirSign;
        if (isHorizontal) {
          const max = node.scrollWidth - node.clientWidth;
          let next = node.scrollLeft + delta;
          if (next > max) next = 0;
          else if (next < 0) next = max;
          node.scrollLeft = next;
        } else {
          const max = node.scrollHeight - node.clientHeight;
          let next = node.scrollTop + delta;
          if (next > max) next = 0;
          else if (next < 0) next = max;
          node.scrollTop = next;
        }
      }
      rafRef.current = requestAnimationFrame(tick);
    };

    rafRef.current = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(rafRef.current);
      node.removeEventListener("mouseenter", onEnter);
      node.removeEventListener("mouseleave", onLeave);
      document.removeEventListener("visibilitychange", onVisibility);
      lastTsRef.current = 0;
      pausedRef.current = false;
    };
  }, [enabled, direction, speed, pauseOnHover]);

  const setRef = (node) => { nodeRef.current = node; };
  return setRef;
};

export default useAutoScroll;
